import { useState } from 'react';
import {
  Edit2, Trash2, Copy, Check, X, ChevronDown, ChevronUp,
  ExternalLink, Calendar, MapPin, Globe, Package, Plane, Bus,
  Image, AlertCircle, DollarSign
} from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const PackageRow = ({ pkg, onEdit, onDelete, onDuplicate, isDuplicating, isSelected, onToggleSelect, onPriceUpdate }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [inlinePrice, setInlinePrice] = useState(pkg.price || '');
  const [inlineOldPrice, setInlineOldPrice] = useState(pkg.old_price || '');
  const [savingPrice, setSavingPrice] = useState(false);

  // Check if program is structured (object) or simple (string)
  const hasStructuredProgram = pkg.detailed_program?.length > 0 && 
    typeof pkg.detailed_program[0] === 'object';

  const isActive = pkg.is_active !== false;

  const handleInlinePriceSave = async () => {
    if (!inlinePrice.trim() || inlinePrice === pkg.price && inlineOldPrice === (pkg.old_price || '')) {
      setEditingPrice(false);
      return;
    }
    setSavingPrice(true);
    try {
      if (onPriceUpdate) {
        await onPriceUpdate(pkg.id, inlinePrice.trim(), inlineOldPrice.trim() || null);
      }
      setEditingPrice(false);
    } catch (e) {
      console.error('Price save error:', e);
    }
    setSavingPrice(false);
  };

  return (
    <>
      <tr className={`border-b border-navy/5 hover:bg-navy/5 transition-colors ${!isActive ? 'opacity-60' : ''} ${isSelected ? 'bg-red-50' : ''}`}>
        <td className="py-3 px-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 rounded border-navy/30 text-red-600 focus:ring-red-500"
          />
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              {pkg.image_url ? (
                <img src={pkg.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center">
                  <Image className="w-5 h-5 text-navy/40" />
                </div>
              )}
              {/* Active/Inactive indicator */}
              <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${isActive ? 'bg-emerald-500' : 'bg-red-500'}`} title={isActive ? 'Activ' : 'Inactiv'} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-navy text-sm line-clamp-1">{pkg.title}</p>
                {!isActive && (
                  <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-xs font-medium">
                    Inactiv
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-navy/50">
                <span>{pkg.id?.substring(0, 8)}...</span>
                {pkg.transport_type && (
                  <span className="flex items-center gap-1">
                    {pkg.transport_type === 'avion' ? <Plane className="w-3 h-3" /> : <Bus className="w-3 h-3" />}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>
        <td className="py-3 px-4 text-sm text-navy">{pkg.operator}</td>
        <td className="py-3 px-4">
          {editingPrice ? (
            <div className="flex flex-col gap-1" data-testid={`inline-price-edit-${pkg.id}`}>
              <input
                type="text"
                value={inlinePrice}
                onChange={e => setInlinePrice(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleInlinePriceSave(); if (e.key === 'Escape') { setEditingPrice(false); setInlinePrice(pkg.price); setInlineOldPrice(pkg.old_price || ''); } }}
                className="w-28 px-2 py-1 border border-gold rounded text-sm font-semibold text-navy focus:outline-none focus:ring-1 focus:ring-gold"
                placeholder="Preț nou"
                autoFocus
              />
              <input
                type="text"
                value={inlineOldPrice}
                onChange={e => setInlineOldPrice(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleInlinePriceSave(); if (e.key === 'Escape') { setEditingPrice(false); setInlinePrice(pkg.price); setInlineOldPrice(pkg.old_price || ''); } }}
                className="w-28 px-2 py-1 border border-navy/20 rounded text-xs text-navy/50 focus:outline-none"
                placeholder="Preț vechi"
              />
              <div className="flex gap-1">
                <button onClick={handleInlinePriceSave} disabled={savingPrice}
                  className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Salvează">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => { setEditingPrice(false); setInlinePrice(pkg.price); setInlineOldPrice(pkg.old_price || ''); }}
                  className="p-0.5 text-red-500 hover:bg-red-50 rounded" title="Anulează">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="cursor-pointer group" onClick={() => setEditingPrice(true)} title="Click pentru a edita prețul" data-testid={`price-cell-${pkg.id}`}>
              <span className="font-semibold text-gold group-hover:underline">{pkg.price}</span>
              {pkg.old_price && (
                <span className="ml-2 text-xs text-navy/40 line-through">{pkg.old_price}</span>
              )}
              <DollarSign className="w-3 h-3 text-navy/20 group-hover:text-gold inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </td>
        <td className="py-3 px-4">
          <div className="flex flex-col gap-1">
            <span className="px-2 py-1 bg-navy/5 rounded text-navy/70 text-xs">
              {pkg.category}
            </span>
            {pkg.country && (
              <span className="px-2 py-0.5 bg-gold/10 rounded text-gold text-xs">
                {pkg.country}
              </span>
            )}
            {pkg.departure_city && (
              <span className="px-2 py-0.5 bg-blue-100 rounded text-blue-700 text-xs flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {pkg.departure_city}
              </span>
            )}
          </div>
        </td>
        <td className="py-3 px-4">
          <div className="flex flex-col gap-1 text-xs">
            <span className="text-navy/60">{pkg.duration || '-'}</span>
            {pkg.departure_dates?.length > 0 && (
              <span className="text-navy/40">{pkg.departure_dates.length} date</span>
            )}
          </div>
        </td>
        <td className="py-3 px-4 text-center">
          <span className="font-bold text-navy">{pkg.click_count || 0}</span>
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setExpanded(!expanded)}
              variant="ghost"
              size="sm"
              className="text-navy/60 hover:bg-navy/5"
              title="Vezi detalii"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button
              onClick={() => onEdit(pkg)}
              variant="ghost"
              size="sm"
              className="text-gold hover:bg-gold/10"
              data-testid={`edit-package-${pkg.id}`}
              title="Editează"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => onDuplicate(pkg.id)}
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:bg-blue-50"
              data-testid={`duplicate-package-${pkg.id}`}
              title="Duplică"
              disabled={isDuplicating}
            >
              {isDuplicating ? (
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            {showDeleteConfirm ? (
              <div className="flex items-center gap-1">
                <Button
                  onClick={() => {
                    onDelete(pkg.id);
                    setShowDeleteConfirm(false);
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="ghost"
                  size="sm"
                  className="text-navy/60 hover:bg-navy/5"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="ghost"
                size="sm"
                className="text-red-500 hover:bg-red-50"
                data-testid={`delete-package-${pkg.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </td>
      </tr>
      
      {/* Expanded Details Row */}
      {expanded && (
        <tr className="bg-navy/5">
          <td colSpan="6" className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {/* Column 1: Basic Info */}
              <div className="space-y-3">
                <h4 className="font-semibold text-navy flex items-center gap-2">
                  <Package className="w-4 h-4 text-gold" /> Informații de bază
                </h4>
                
                <div className="space-y-2 text-navy/70">
                  <div><strong>Durată:</strong> {pkg.duration || '-'}</div>
                  <div><strong>Transport:</strong> {pkg.transport_type === 'avion' ? 'Avion' : pkg.transport_type === 'autocar' ? 'Autocar' : pkg.transport_type === 'individual' ? 'Individual' : pkg.transport_type || '-'}</div>
                  <div><strong>Țară:</strong> {pkg.country || '-'}</div>
                  <div><strong>Continent:</strong> {pkg.continent || '-'}</div>
                  {pkg.short_description && (
                    <div className="mt-2">
                      <strong>Descriere:</strong>
                      <p className="text-xs text-navy/60 mt-1 line-clamp-3">{pkg.short_description}</p>
                    </div>
                  )}
                </div>
                
                {/* Departure Dates */}
                {pkg.departure_dates?.length > 0 && (
                  <div>
                    <strong className="text-navy">Date de plecare ({pkg.departure_dates.length}):</strong>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {pkg.departure_dates.slice(0, 6).map((date, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gold/10 text-gold rounded text-xs">
                          {date}
                        </span>
                      ))}
                      {pkg.departure_dates.length > 6 && (
                        <span className="text-navy/40 text-xs">+{pkg.departure_dates.length - 6} mai multe</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Column 2: Services */}
              <div className="space-y-3">
                {/* Includes */}
                {pkg.includes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-navy flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" /> Include ({pkg.includes.length})
                    </h4>
                    <ul className="mt-2 space-y-1 text-xs text-navy/70 max-h-32 overflow-y-auto">
                      {pkg.includes.slice(0, 5).map((item, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-emerald-500 mt-0.5">•</span>
                          <span className="line-clamp-2">{item}</span>
                        </li>
                      ))}
                      {pkg.includes.length > 5 && (
                        <li className="text-navy/40 italic">+{pkg.includes.length - 5} mai multe...</li>
                      )}
                    </ul>
                    {pkg.includes_note && (
                      <p className="mt-1 text-xs text-navy/50 italic line-clamp-2">📝 {pkg.includes_note}</p>
                    )}
                  </div>
                )}
                
                {/* Excludes */}
                {pkg.excludes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-navy flex items-center gap-2">
                      <X className="w-4 h-4 text-red-500" /> Nu include ({pkg.excludes.length})
                    </h4>
                    <ul className="mt-2 space-y-1 text-xs text-navy/70 max-h-24 overflow-y-auto">
                      {pkg.excludes.slice(0, 4).map((item, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-red-500 mt-0.5">•</span>
                          <span className="line-clamp-1">{item}</span>
                        </li>
                      ))}
                      {pkg.excludes.length > 4 && (
                        <li className="text-navy/40 italic">+{pkg.excludes.length - 4} mai multe...</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Column 3: Program & Excursions */}
              <div className="space-y-3">
                {/* Detailed Program */}
                {pkg.detailed_program?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-navy flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gold" /> Program ({pkg.detailed_program.length} zile)
                      {hasStructuredProgram && <span className="text-xs text-emerald-500">✓ Structurat</span>}
                    </h4>
                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                      {pkg.detailed_program.slice(0, 4).map((day, i) => (
                        <div key={i} className="text-xs p-2 bg-white/50 rounded">
                          {typeof day === 'object' ? (
                            <>
                              <div className="font-semibold text-navy">
                                {day.day}: {day.title?.substring(0, 40)}{day.title?.length > 40 ? '...' : ''}
                              </div>
                              {day.accommodation && (
                                <div className="text-navy/50 mt-0.5">🏨 {day.accommodation}</div>
                              )}
                              {day.meals && (
                                <div className="text-navy/50">🍽️ {day.meals}</div>
                              )}
                            </>
                          ) : (
                            <div className="text-navy/70 line-clamp-2">{day}</div>
                          )}
                        </div>
                      ))}
                      {pkg.detailed_program.length > 4 && (
                        <p className="text-xs text-navy/40 italic">+{pkg.detailed_program.length - 4} zile mai multe...</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Optional Excursions */}
                {pkg.optional_excursions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-navy flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gold" /> Excursii opționale ({pkg.optional_excursions.length})
                    </h4>
                    <div className="mt-2 space-y-1">
                      {pkg.optional_excursions.slice(0, 4).map((exc, i) => (
                        <div key={i} className="flex justify-between items-center text-xs bg-white/50 px-2 py-1 rounded">
                          <span className="text-navy/70 line-clamp-1 flex-1">{exc.name}</span>
                          <span className="text-gold font-semibold ml-2">{exc.price}</span>
                        </div>
                      ))}
                      {pkg.optional_excursions.length > 4 && (
                        <p className="text-xs text-navy/40 italic">+{pkg.optional_excursions.length - 4} mai multe...</p>
                      )}
                    </div>
                    {pkg.optional_excursions_note && (
                      <p className="mt-1 text-xs text-navy/50 italic line-clamp-2">📝 {pkg.optional_excursions_note}</p>
                    )}
                  </div>
                )}
                
                {/* No detailed data message */}
                {!pkg.includes?.length && !pkg.detailed_program?.length && (
                  <div className="text-center py-4 text-navy/40 text-sm">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Nu există date detaliate pentru acest pachet</p>
                    <p className="text-xs mt-1">Editați pachetul pentru a adăuga informații</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Link to detail page */}
            <div className="mt-4 pt-3 border-t border-navy/10 flex justify-between items-center">
              <a 
                href={pkg.redirect_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-gold hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> Pagina operatorului
              </a>
              <a 
                href={`/pachet/${pkg.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-navy/60 hover:text-navy flex items-center gap-1"
              >
                Vezi în site <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};


export { PackageRow };
